/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { IonItem } from "@ionic/react";
import {
  CompetenceActivityClass,
  EvidencePart,
  INOSSelectCompetence,
  INOSSelectLearningObjectives,
  LearningObjective,
} from "@interfaces/Interfaces";
import { Button_Collapse, Button_Expand } from "@assets/images";
import NOSSelectContainer from "@components/common/NOSSelectContainer";
import _ from "lodash";
import LearningObjectiveSelector from "./LearningObjectiveSelector";
import { Text, View } from "@react-pdf/renderer";
import * as ProgramUtils from "@utils/programUtils";
import classNames from "classnames";

type Props = {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  definition: EvidencePart;
  mandatoryStandards?: CompetenceActivityClass[];
  optionalStandards?: CompetenceActivityClass[];
  onValueChanged?: (value: any) => void;
  setCollapsibleOpen: (value: boolean) => void;
  toggleCollapsible: () => void;
  collapsibleOpen: boolean;
  data?: any;
  isDisplay?: boolean;
  isPDF?: boolean;
  pdfStyles: any;
  initialValue?: any;
  expandStandardId?: string | null;
};

const NOSSelect: React.FC<Props> = (props) => {
  const [collapsibleOpen, setCollapsibleOpen] = useState<string[]>(
    props.expandStandardId ? [props.expandStandardId] : []
  );
  const [selectedStandards, setSelectedStandards] = useState<INOSSelectCompetence[]>(props.initialValue?.Comps ?? []);
  const [selectedLearningObjectives, setSelectedLearningObjectives] = useState<INOSSelectLearningObjectives[]>(
    props.initialValue?.LOs ?? []
  );
  const [showDescriptions, setShowDescriptions] = useState<boolean>(true);

  useEffect(() => {
    if (props.expandStandardId) {
      setCollapsibleOpen([props.expandStandardId]);
    }
  }, [props.expandStandardId]);

  if (props.isPDF) {
    return (
      <View>
        {props.data.Comps?.length > 0 && (
          <View>
            {props.data.Comps.map((item: INOSSelectCompetence) => {
              const learningObjectives = ProgramUtils.getLearningObjectivesForStandard(
                props.mandatoryStandards,
                props.optionalStandards,
                item,
                props.data.LOs
              );

              return (
                <View key={item.id}>
                  <View>
                    <Text style={props.pdfStyles.reflectionHeader}>{"Standard"}</Text>
                    <Text style={props.pdfStyles.evidenceDate}>{item.Name}</Text>
                  </View>
                  <View>
                    <Text style={props.pdfStyles.reflectionHeader}>{"PCKUs"}</Text>
                    <Text style={props.pdfStyles.evidenceDate}>
                      {learningObjectives && learningObjectives?.length > 0
                        ? learningObjectives
                            .map((item) => item.displayName)
                            .sort()
                            .join(", ")
                        : "None"}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </View>
    );
  }

  if (props.isDisplay) {
    return (
      <div>
        {props.data.Comps?.length > 0 && (
          <div>
            {props.data.Comps.map((item: INOSSelectCompetence) => {
              const learningObjectives = ProgramUtils.getLearningObjectivesForStandard(
                props.mandatoryStandards,
                props.optionalStandards,
                item,
                props.data.LOs
              );

              return (
                <div
                  key={item.id}
                  className="readEvidenceBlock"
                >
                  <div>
                    <div className="readEvidenceHeader">{"Standard"}</div>
                    <div className="readEvidenceText">{item.Name}</div>
                    <div className="h-[1px] w-full bg-grey-30 mt-4" />
                  </div>
                  <div>
                    <div className="readEvidenceHeader mt-5">{"PCKUs"}</div>
                    <div>
                      {learningObjectives && learningObjectives?.length > 0
                        ? learningObjectives
                            .map((item) => item.displayName)
                            .sort()
                            .join(", ")
                        : "None"}
                    </div>
                    <div className="h-[1px] w-full bg-grey-30 mt-4" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const handleCollapsibleToggle = (id: string, close?: boolean): void => {
    const array = _.cloneDeep(collapsibleOpen);
    const index = array.findIndex((item) => item === id);

    if (index > -1) {
      array.splice(index, 1);
    } else {
      !close && array.push(id);
    }

    setCollapsibleOpen(array);
  };

  const toggleLearningObjective = (learningObjective: LearningObjective): void => {
    const array = _.cloneDeep(selectedLearningObjectives);
    const index = array.findIndex((item) => item.id === learningObjective.ID);

    if (index > -1) {
      array.splice(index, 1);
    } else {
      array.push({ id: learningObjective.ID, displayName: learningObjective["Display Name"]! });
    }

    const allStandards = [...(props.mandatoryStandards ?? []), ...(props.optionalStandards ?? [])];
    let updatedStandards = _.cloneDeep(selectedStandards);

    // This block of code is to add standards to the selected standards array if a learning objective is added or removed
    // As the checkbox was removed as it was difficult for users to understand that they needed to select LOs for a standard
    if (index > -1) {
      // If a learning objective was removed, check if any standards have no associated LOs, and remove them from the selected standards
      updatedStandards = updatedStandards.filter((selectedStandard) => {
        const standard = allStandards.find((item) => item.ID === selectedStandard.id);
        if (!standard) return true;

        const standardLOIds = standard["Learning Objective"]?.map((lo) => lo.ID) ?? [];
        const hasAssociatedLOs = array.some((lo) => standardLOIds.includes(lo.id));

        return hasAssociatedLOs;
      });
    } else {
      // If a learning objective was added, find and add the associated standard(s) if not already added
      const standardsContainingLO = allStandards.filter((standard) => {
        const standardLOIds = standard["Learning Objective"]?.map((lo) => lo.ID) ?? [];
        return standardLOIds.includes(learningObjective.ID);
      });

      standardsContainingLO.forEach((standard) => {
        const isAlreadySelected = updatedStandards.some((selectedStandard) => selectedStandard.id === standard.ID);
        if (!isAlreadySelected) {
          updatedStandards.push({ id: standard.ID, Name: standard.Name });
        }
      });
    }

    const value = {
      Comps: [...updatedStandards],
      LOs: [...array],
    };

    props.onValueChanged?.(value);

    setSelectedLearningObjectives(array);
    setSelectedStandards(updatedStandards);
  };

  const selectAllLearningObjectives = (mandatoryStandard: CompetenceActivityClass): void => {
    let array = _.cloneDeep(selectedLearningObjectives);

    const learningObjectives: INOSSelectLearningObjectives[] =
      mandatoryStandard["Learning Objective"]?.map((learningObjective) => ({
        id: learningObjective.ID,
        displayName: learningObjective["Display Name"]!,
      })) ?? [];

    if (learningObjectives.length > 0) {
      array.push(...learningObjectives);
    }

    array = _.uniqBy(array, "id");

    const updatedStandards = _.cloneDeep(selectedStandards);
    const isAlreadySelected = updatedStandards.some((selectedStandard) => selectedStandard.id === mandatoryStandard.ID);
    if (!isAlreadySelected) {
      updatedStandards.push({ id: mandatoryStandard.ID, Name: mandatoryStandard.Name });
    }

    setSelectedLearningObjectives(array);
    setSelectedStandards(updatedStandards);

    const value = {
      Comps: [...updatedStandards],
      LOs: [...array],
    };

    props.onValueChanged?.(value);
  };

  const deselectAllLearningObjectives = (mandatoryStandard: CompetenceActivityClass): void => {
    let array = _.cloneDeep(selectedLearningObjectives);

    const learningObjectives: string[] =
      mandatoryStandard["Learning Objective"]?.map((learningObjective) => learningObjective.ID) ?? [];

    if (learningObjectives.length > 0) {
      array = array.filter((learningObjective) => !learningObjectives.includes(learningObjective.id));
    }

    array = _.uniqBy(array, "id");

    const standardLOIds = mandatoryStandard["Learning Objective"]?.map((lo) => lo.ID) ?? [];
    const hasAssociatedLOs = array.some((lo) => standardLOIds.includes(lo.id));
    let updatedStandards = _.cloneDeep(selectedStandards);
    if (!hasAssociatedLOs) {
      updatedStandards = updatedStandards.filter((selectedStandard) => selectedStandard.id !== mandatoryStandard.ID);
    }

    setSelectedLearningObjectives(array);
    setSelectedStandards(updatedStandards);

    const value = {
      Comps: [...updatedStandards],
      LOs: [...array],
    };

    props.onValueChanged?.(value);
  };

  const hasOptionalStandards = useMemo(
    () => props.optionalStandards && props.optionalStandards?.length > 0,
    [props.optionalStandards]
  );

  return (
    <div>
      <div className="reflectionContentCard bg-grey-20">
        <div className="reflectionContentCardTitle !mb-0">
          {`Select the relevant NOS for this evidence`}
          {(props.definition.options === "Mandatory" || props.definition["option type"] === "Mandatory") && (
            <span className="text-cta-red"> (Mandatory)</span>
          )}
        </div>
      </div>
      {props.mandatoryStandards?.map((mandatoryStandard: CompetenceActivityClass, index: number) => {
        const selected = selectedStandards.findIndex((item) => item.id === mandatoryStandard.ID) > -1;

        return (
          <NOSSelectContainer
            key={mandatoryStandard.ID}
            bottomMargin={index !== props.mandatoryStandards!.length - 1 || hasOptionalStandards}
          >
            <div className="relative">
              <IonItem
                button
                detail={false}
                className="evidencePartsCheckboxContainer"
                onClick={() => handleCollapsibleToggle(mandatoryStandard.ID)}
              >
                <div className="w-full flex flex-row items-center !justify-between px-3.5 py-[1px]">
                  <div className="my-1">
                    <div className="text-[17px] font-acc-600 leading-default tracking-[-0.3px] text-grey-90 whitespace-nowrap overflow-hidden text-ellipsis acc-underline decoration-current">
                      {mandatoryStandard.Name}
                    </div>
                    <div
                      className={classNames(
                        "text-[15px] font-acc-500 leading-default tracking-[-0.3px] text-grey-70 overflow-hidden text-ellipsis acc-underline decoration-current mt-[2px] line-clamp-3",
                        {
                          "!pr-14": selected,
                        }
                      )}
                    >
                      {mandatoryStandard.Description}
                    </div>
                  </div>
                  <button
                    className="z-50 flex-shrink-0"
                    onClick={() => handleCollapsibleToggle(mandatoryStandard.ID)}
                  >
                    <img
                      alt=""
                      className="h-icon w-icon img-no-select"
                      src={collapsibleOpen.includes(mandatoryStandard.ID) ? Button_Collapse : Button_Expand}
                    />
                  </button>
                </div>
              </IonItem>
            </div>
            {mandatoryStandard["Learning Objective"] && (
              <LearningObjectiveSelector
                learningObjectives={mandatoryStandard["Learning Objective"]}
                collapsibleOpen={collapsibleOpen.includes(mandatoryStandard.ID)}
                onValueChanged={(learningObjective) => toggleLearningObjective(learningObjective)}
                selectedLearningObjectives={selectedLearningObjectives}
                showDescription={showDescriptions}
                toggleDescriptions={() => setShowDescriptions(!showDescriptions)}
                selectAllLearningObjectives={() => selectAllLearningObjectives(mandatoryStandard)}
                deselectAllLearningObjectives={() => deselectAllLearningObjectives(mandatoryStandard)}
              />
            )}
          </NOSSelectContainer>
        );
      })}
      {props.optionalStandards?.map((optionalStandard: CompetenceActivityClass, index: number) => {
        const selected = selectedStandards.findIndex((item) => item.id === optionalStandard.ID) > -1;

        return (
          <NOSSelectContainer
            key={optionalStandard.ID}
            bottomMargin={index !== props.optionalStandards!.length - 1}
          >
            <div className="relative">
              <IonItem
                button
                detail={false}
                className="evidencePartsCheckboxContainer"
                onClick={() => handleCollapsibleToggle(optionalStandard.ID)}
              >
                <div className="w-full flex flex-row items-center !justify-between px-3.5 py-[1px]">
                  <div className="my-1">
                    <div className="text-[17px] font-acc-600 leading-default tracking-[-0.3px] text-grey-90 whitespace-nowrap overflow-hidden text-ellipsis acc-underline decoration-current">
                      {optionalStandard.Name}
                    </div>
                    <div
                      className={classNames(
                        "text-[15px] font-acc-500 leading-default tracking-[-0.3px] text-grey-70 overflow-hidden text-ellipsis acc-underline decoration-current mt-[2px] line-clamp-3 w-full",
                        {
                          "!pr-14": selected,
                        }
                      )}
                    >
                      {optionalStandard.Description}
                    </div>
                  </div>
                  <button
                    className="z-50 flex-shrink-0"
                    onClick={() => handleCollapsibleToggle(optionalStandard.ID)}
                  >
                    <img
                      alt=""
                      className="h-icon w-icon img-no-select"
                      src={collapsibleOpen.includes(optionalStandard.ID) ? Button_Collapse : Button_Expand}
                    />
                  </button>
                </div>
              </IonItem>
            </div>
            {optionalStandard["Learning Objective"] && (
              <LearningObjectiveSelector
                learningObjectives={optionalStandard["Learning Objective"]}
                collapsibleOpen={collapsibleOpen.includes(optionalStandard.ID)}
                onValueChanged={(learningObjective) => toggleLearningObjective(learningObjective)}
                selectedLearningObjectives={selectedLearningObjectives}
                showDescription={showDescriptions}
                toggleDescriptions={() => setShowDescriptions(!showDescriptions)}
                selectAllLearningObjectives={() => selectAllLearningObjectives(optionalStandard)}
                deselectAllLearningObjectives={() => deselectAllLearningObjectives(optionalStandard)}
              />
            )}
          </NOSSelectContainer>
        );
      })}
    </div>
  );
};

export default NOSSelect;
